import { useState, useEffect, useCallback } from 'react';
import {
  isStorageAccessAPISupported,
  needsStorageAccess as checkNeedsStorageAccess,
  isThirdPartyContext,
  mayBlockThirdPartyCookies,
} from '../utilities/browserDetection';

export interface StorageAccessState {
  /**
   * Whether the app currently has storage access
   */
  hasAccess: boolean;

  /**
   * Whether the app needs storage access (in third-party iframe context)
   */
  needsAccess: boolean;

  /**
   * Whether the Storage Access API is supported by the browser
   */
  isSupported: boolean;

  /**
   * Whether the app is running in an iframe
   */
  isIframe: boolean;

  /**
   * Whether a storage access request is currently pending
   */
  isRequesting: boolean;

  /**
   * Error from last storage access request, if any
   */
  error: string | null;

  /**
   * Request storage access (must be called from user gesture handler)
   */
  requestAccess: () => Promise<boolean>;

  /**
   * Check current storage access status
   */
  checkAccess: () => Promise<boolean>;
}

/**
 * Hook for managing Storage Access API state and requests
 * Used to handle Safari's third-party cookie restrictions in iframe contexts
 */
export function useStorageAccess(): StorageAccessState {
  const [hasAccess, setHasAccess] = useState<boolean>(false);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const isSupported = isStorageAccessAPISupported();
  const needsAccess = checkNeedsStorageAccess();
  const isIframe = isThirdPartyContext();

  /**
   * Check if we currently have storage access
   */
  const checkAccess = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      // If API not supported, assume we have access (non-Safari browser)
      setHasAccess(true);
      return true;
    }

    try {
      const access = await document.hasStorageAccess();
      setHasAccess(access);
      return access;
    } catch (err) {
      console.error('Error checking storage access:', err);
      setHasAccess(false);
      return false;
    }
  }, [isSupported]);

  /**
   * Request storage access from the browser
   * Must be called from a user gesture event handler (click, touch, etc.)
   */
  const requestAccess = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      console.warn('Storage Access API not supported');
      setError('Storage Access API not supported in this browser');
      return false;
    }

    if (!needsAccess) {
      // Not in third-party context, no need to request
      setHasAccess(true);
      return true;
    }

    setIsRequesting(true);
    setError(null);

    try {
      // Request storage access - this must be called from a user gesture
      await document.requestStorageAccess();

      // Verify we actually got access
      const access = await document.hasStorageAccess();
      setHasAccess(access);
      setIsRequesting(false);

      if (!access) {
        const errorMsg = 'Storage access was not granted';
        setError(errorMsg);
        console.warn(errorMsg);
      }

      return access;
    } catch (err: any) {
      setIsRequesting(false);

      // Handle different error scenarios
      let errorMsg = 'Failed to request storage access';

      if (err.name === 'NotAllowedError') {
        // User denied permission or hasn't interacted with the domain before
        errorMsg = 'Storage access denied. You may need to visit this site directly first.';
      } else if (err.name === 'InvalidStateError') {
        // Document not active or other state issue
        errorMsg = 'Cannot request storage access in current state';
      }

      console.error('Storage access request failed:', err);
      setError(errorMsg);
      setHasAccess(false);
      return false;
    }
  }, [isSupported, needsAccess]);

  // Check storage access on mount
  useEffect(() => {
    if (needsAccess) {
      checkAccess();
    } else if (!isIframe) {
      // Not in iframe, we have access
      setHasAccess(true);
    } else if (!mayBlockThirdPartyCookies()) {
      // In iframe but browser doesn't block third-party cookies
      setHasAccess(true);
    }
  }, [checkAccess, needsAccess, isIframe]);

  return {
    hasAccess,
    needsAccess,
    isSupported,
    isIframe,
    isRequesting,
    error,
    requestAccess,
    checkAccess,
  };
}
